import { useEffect, useState } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import type { ActivityCardActionId } from "./activityCardActions";
import type { ActivityEvent, ActivityLane } from "./activitiesTypes";
import { ActivityCard } from "./ActivityCard";
import { activityLaneTitleKey } from "./activityPresentation";

const STACK_PEEK_PX = 10;
const MAX_STACK_LAYERS = 4;

export function activityLaneStackPadBottom(eventCount: number): number {
  if (eventCount <= 1) return 0;
  return Math.min(eventCount - 1, MAX_STACK_LAYERS - 1) * STACK_PEEK_PX;
}

export function ActivityLaneSection({
  lane,
  events,
  rowStackPadBottom,
  selectedId,
  onSelect,
  onAction,
  isActionBusy,
}: {
  readonly lane: ActivityLane;
  readonly events: readonly ActivityEvent[];
  readonly rowStackPadBottom: number;
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
  readonly onAction?: (actionId: ActivityCardActionId, event: ActivityEvent) => void;
  readonly isActionBusy?: (eventId: string, actionId: ActivityCardActionId) => boolean;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setExpanded(false);
  }, [events]);
  const stack = events.slice(0, MAX_STACK_LAYERS);
  const front = stack[0] ?? null;
  const behind = stack.slice(1);
  const stackPadBottom = activityLaneStackPadBottom(events.length);
  const laneTitle = t(activityLaneTitleKey(lane));
  const cardSlotMinHeight = `calc(10.5rem + ${rowStackPadBottom}px)`;
  const moreCount = events.length - 1;
  const showStackToggle = events.length > 1;

  return (
    <section className="flex h-full min-w-0 flex-col gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {laneTitle}
        <span className="ms-1.5 font-normal text-muted-foreground/70">({events.length})</span>
      </h2>

      {events.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border/70 px-3 py-8 text-center text-xs text-muted-foreground">
          {t("activities.lane.empty")}
        </p>
      ) : expanded ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <ScrollArea scrollFade className="max-h-[min(28rem,55dvh)] min-h-0">
            <ul className="flex flex-col gap-2 pe-1">
              {events.map((event) => (
                <li key={event.id}>
                  <ActivityCard
                    event={event}
                    selected={selectedId === event.id}
                    onSelect={() => onSelect(event.id)}
                    onAction={onAction}
                    isActionBusy={(actionId) => isActionBusy?.(event.id, actionId) ?? false}
                    uniformHeight={false}
                  />
                </li>
              ))}
            </ul>
          </ScrollArea>
          {showStackToggle ? (
            <Button
              type="button"
              variant="outline"
              size="compact"
              className="h-8 w-full shrink-0 text-[11px] text-muted-foreground"
              onClick={() => setExpanded(false)}
            >
              {t("activities.lane.showStack")}
            </Button>
          ) : null}
        </div>
      ) : front ? (
        <div className="flex flex-col gap-2">
          <div
            className={cn(
              "relative w-full min-w-0 motion-reduce:animate-none animate-morrow-page-enter",
            )}
            style={{ minHeight: cardSlotMinHeight, paddingBottom: stackPadBottom }}
          >
            {behind.map((event, index) => {
              const depth = index + 1;
              return (
                <div
                  key={event.id}
                  className="pointer-events-none absolute inset-x-0 top-0 h-[10.5rem]"
                  style={{
                    transform: `translateY(${depth * STACK_PEEK_PX}px)`,
                    zIndex: stack.length - depth,
                    opacity: Math.max(0.45, 0.92 - depth * 0.12),
                  }}
                >
                  {/* Just the card's edge: cards are see-through glass, so a full card here
                      would show its text through the one in front. */}
                  <Card
                    aria-hidden
                    className="h-full border-border/70 shadow-sm ring-1 ring-border/30"
                  />
                </div>
              );
            })}
            <div className="relative h-[10.5rem]" style={{ zIndex: stack.length + 1 }}>
              <ActivityCard
                event={front}
                selected={selectedId === front.id}
                onSelect={() => onSelect(front.id)}
                onAction={onAction}
                isActionBusy={(actionId) => isActionBusy?.(front.id, actionId) ?? false}
                className="h-full overflow-hidden shadow-sm"
                uniformHeight={false}
              />
            </div>
            {showStackToggle ? (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center"
                style={{ zIndex: stack.length + 2 }}
              >
                <Button
                  type="button"
                  variant="secondary"
                  size="compact"
                  className="pointer-events-auto h-7 -translate-y-1 shadow-sm px-2.5 text-[11px] font-medium tabular-nums"
                  onClick={() => setExpanded(true)}
                  aria-label={t("activities.lane.expandStack", { count: events.length })}
                >
                  {t("activities.lane.moreCount", { count: moreCount })}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
